import moment from 'moment';
import { alpha } from '@mui/material/styles';
import { isBlockingBookingStatus } from '../../utils/bookingUtils';

export function computeDaySnapshot(data, dateStr, totalRooms) {
  const empty = {
    bookingCount: 0,
    occupied: 0,
    totalRooms,
    occupancyPct: 0,
    occupancyPctRaw: 0,
    blockedRooms: 0,
  };
  if (!totalRooms) return empty;

  const byDate = data?.booking_items_by_date?.[dateStr];
  const occupiedRoomIds = new Set();
  const bookingIds = new Set();

  if (byDate && typeof byDate === 'object') {
    for (const roomTypeBlock of Object.values(byDate)) {
      if (!roomTypeBlock || typeof roomTypeBlock !== 'object') continue;
      for (const [roomKey, items] of Object.entries(roomTypeBlock)) {
        if (!Array.isArray(items)) continue;
        const roomNum = Number(roomKey);
        for (const item of items) {
          const st = item.item_status || item.status;
          if (!isBlockingBookingStatus(st)) continue;
          if (Number.isFinite(roomNum) && roomNum > 0) {
            occupiedRoomIds.add(roomNum);
          }
          if (item.booking_id != null) bookingIds.add(item.booking_id);
        }
      }
    }
  }

  let blockedRooms = 0;
  const blocked = data?.blocked_rooms_by_date?.[dateStr];
  if (blocked && typeof blocked === 'object') {
    for (const o of Object.values(blocked)) {
      if (o && typeof o.num_rooms === 'number') blockedRooms += o.num_rooms;
    }
  }

  const occupied = occupiedRoomIds.size;
  const occupancyPctRaw = totalRooms > 0 ? (occupied / totalRooms) * 100 : 0;
  const occupancyPct = Math.min(100, Math.round(occupancyPctRaw));

  return {
    bookingCount: bookingIds.size,
    occupied,
    totalRooms,
    occupancyPct,
    occupancyPctRaw,
    blockedRooms,
  };
}

export function countArrivalsOnDate(data, dateStr) {
  let n = 0;
  for (const b of data?.bookings || []) {
    const cin = (b.check_in_date || b.start_date || b.start || '').toString().slice(0, 10);
    if (cin !== dateStr) continue;
    const st = String(b.status || '').toLowerCase().replace(/\s+/g, '_');
    if (['cancelled', 'canceled', 'no_show'].includes(st)) continue;
    n += 1;
  }
  return n;
}

export function buildDayStat(inventoryByDate, data, dateStr, totalRooms) {
  const arrivals = countArrivalsOnDate(data, dateStr);
  const fallback = computeDaySnapshot(data, dateStr, totalRooms);

  if (inventoryByDate != null) {
    const row = inventoryByDate[dateStr];
    const capacity = Number(row?.total) || 0;
    const bookedNights = Number(row?.booked) || 0;
    const bookedPctRaw = capacity > 0 ? (bookedNights / capacity) * 100 : 0;
    return {
      mode: 'inventory',
      arrivals,
      bookedNights,
      capacity,
      directBooked: Number(row?.direct_booked) || 0,
      onlineBooked: Number(row?.online_booked) || 0,
      cancelled: Number(row?.cancelled) || 0,
      bookedPctRaw,
      barPct: Math.min(100, bookedPctRaw),
      blockedRooms: fallback.blockedRooms,
    };
  }

  return {
    mode: 'legacy',
    arrivals,
    bookingCount: fallback.bookingCount,
    occupied: fallback.occupied,
    totalRooms: fallback.totalRooms,
    occupancyPct: fallback.occupancyPct,
    occupancyPctRaw: fallback.occupancyPctRaw,
    blockedRooms: fallback.blockedRooms,
  };
}

export function getLoadPctRaw(snap) {
  if (!snap) return 0;
  const v =
    snap.mode === 'inventory'
      ? Number(snap.bookedPctRaw)
      : Number(snap.occupancyPctRaw);
  if (!Number.isFinite(v) || v < 0) return 0;
  return v;
}

export function occupancyHeatBackground(pctRaw, isDark, inMonth) {
  if (!inMonth || pctRaw <= 0) return null;
  const p = Math.max(0, pctRaw);

  if (p <= 100) {
    const t = p / 100;
    if (isDark) {
      const a0 = 0.05 + t * 0.38;
      const a1 = 0.08 + t * 0.42;
      return `linear-gradient(145deg, ${alpha('#ffb74d', a0)} 0%, ${alpha('#f57c00', a1)} 42%, ${alpha('#e65100', Math.min(0.55, 0.12 + t * 0.48))} 100%)`;
    }
    const a0 = 0.12 + t * 0.38;
    const a1 = 0.18 + t * 0.45;
    return `linear-gradient(145deg, ${alpha('#ffe0b2', a0)} 0%, ${alpha('#ff9800', a1)} 45%, ${alpha('#e65100', Math.min(0.72, 0.22 + t * 0.5))} 100%)`;
  }

  const over = Math.min(1, (p - 100) / 100);
  if (isDark) {
    return `linear-gradient(145deg, ${alpha('#f57c00', 0.42)} 0%, ${alpha('#ff7043', 0.35 + over * 0.28)} 40%, ${alpha('#e53935', 0.45 + over * 0.35)} 72%, ${alpha('#b71c1c', 0.5 + over * 0.35)} 100%)`;
  }
  return `linear-gradient(145deg, ${alpha('#ff9800', 0.5)} 0%, ${alpha('#ff5722', 0.38 + over * 0.32)} 42%, ${alpha('#e53935', 0.45 + over * 0.35)} 70%, ${alpha('#b71c1c', 0.55 + over * 0.25)} 100%)`;
}

export function heatAccentForBar(pctRaw, isDark) {
  const p = pctRaw || 0;
  if (p <= 0) return isDark ? alpha('#fff', 0.35) : alpha('#000', 0.25);
  if (p > 100) return isDark ? '#ff8a80' : '#c62828';
  if (p >= 75) return isDark ? '#ffb74d' : '#e65100';
  if (p >= 40) return isDark ? '#ffcc80' : '#f57c00';
  if (p >= 15) return isDark ? alpha('#ffe082', 0.95) : '#fb8c00';
  return isDark ? alpha('#ffe0b2', 0.85) : '#ffb74d';
}

export function formatLoadPctLabel(pctRaw) {
  if (pctRaw <= 0) return '';
  if (pctRaw < 10) return pctRaw.toFixed(1);
  if (pctRaw < 100) return String(Math.round(pctRaw));
  if (pctRaw < 1000) return pctRaw.toFixed(1);
  return String(Math.round(pctRaw));
}

/**
 * Mobile dark calendar cells: stay on dark chrome; higher load = lighter surface (not cream/orange wash).
 */
export function mobileDarkOccupancyCellColors(loadPctRaw, inMonth) {
  const emptyOutOfMonth = {
    background: 'rgba(255,255,255,0.01)',
    border: 'rgba(255,255,255,0.04)',
    dayColor: 'rgba(100,116,139,0.45)',
    pctColor: 'rgba(100,116,139,0.45)',
    barColor: 'rgba(100,116,139,0.2)',
    barTrack: 'rgba(255,255,255,0.05)',
  };

  if (!inMonth) return emptyOutOfMonth;

  const p = Math.max(0, Number(loadPctRaw) || 0);

  if (p <= 0) {
    return {
      background: 'rgba(255,255,255,0.035)',
      border: 'rgba(255,255,255,0.07)',
      dayColor: '#f1f5f9',
      pctColor: '#cbd5e1',
      barColor: 'rgba(148,163,184,0.35)',
      barTrack: 'rgba(255,255,255,0.08)',
    };
  }

  if (p > 100) {
    const over = Math.min(1, (p - 100) / 100);
    const lift = 0.12 + over * 0.1;
    return {
      background: `linear-gradient(155deg, rgba(239,68,68,${0.22 + over * 0.14}) 0%, rgba(255,255,255,${lift}) 100%)`,
      border: `rgba(248,113,113,${0.35 + over * 0.25})`,
      dayColor: '#ffffff',
      pctColor: '#fecaca',
      barColor: '#f87171',
      barTrack: 'rgba(0,0,0,0.25)',
    };
  }

  const t = Math.min(1, p / 100);
  const whiteLift = 0.05 + t * 0.15;
  const warmLift = t * 0.06;

  return {
    background: `linear-gradient(155deg, rgba(255,255,255,${whiteLift}) 0%, rgba(251,191,36,${warmLift}) 52%, rgba(148,163,184,${0.04 + t * 0.08}) 100%)`,
    border: `rgba(255,255,255,${0.08 + t * 0.14})`,
    dayColor: '#ffffff',
    pctColor: t >= 0.65 ? '#fffbeb' : t >= 0.3 ? '#fde047' : '#2dd4bf',
    barColor: t >= 0.75 ? '#fbbf24' : t >= 0.4 ? '#f59e0b' : '#2dd4bf',
    barTrack: 'rgba(0,0,0,0.22)',
  };
}

export function mobileDarkOccupancyLegend() {
  return [
    { label: 'Low', background: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.08)' },
    { label: 'Mid', background: 'rgba(255,255,255,0.11)', border: 'rgba(255,255,255,0.12)' },
    { label: 'High', background: 'rgba(255,255,255,0.17)', border: 'rgba(251,191,36,0.35)' },
    { label: 'Over', background: 'rgba(239,68,68,0.28)', border: 'rgba(248,113,113,0.45)' },
  ];
}

export function buildMonthCalendarModel(data, anchorDate, inventoryByDate, totalRooms) {
  const m = moment(anchorDate).startOf('day');
  const monthStart = m.clone().startOf('month');
  const monthEnd = m.clone().endOf('month');
  const gridStart = monthStart.clone().startOf('week');
  const gridEnd = monthEnd.clone().endOf('week');

  const statsByKey = {};
  const cursor = gridStart.clone();
  while (cursor.isSameOrBefore(gridEnd, 'day')) {
    const key = cursor.format('YYYY-MM-DD');
    statsByKey[key] = buildDayStat(inventoryByDate, data, key, totalRooms);
    cursor.add(1, 'day');
  }

  const days = [];
  const dayCursor = gridStart.clone();
  while (dayCursor.isSameOrBefore(gridEnd, 'day')) {
    days.push(dayCursor.clone());
    dayCursor.add(1, 'day');
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return {
    weeks,
    statsByKey,
    monthLabel: monthStart.format('MMMM YYYY'),
    monthStart,
  };
}

export function getDayTrend(statsByKey, day, anchorDate, snap) {
  const inMonth = day.month() === moment(anchorDate).month();
  const prevKey = day.clone().subtract(1, 'day').format('YYYY-MM-DD');
  const prevSnap = statsByKey[prevKey];
  if (!prevSnap || !inMonth) return 'flat';

  if (snap.mode === 'inventory' && prevSnap.mode === 'inventory') {
    if (snap.bookedNights > prevSnap.bookedNights) return 'up';
    if (snap.bookedNights < prevSnap.bookedNights) return 'down';
    return 'flat';
  }
  if (snap.mode === 'legacy' && prevSnap.mode === 'legacy') {
    const cur = getLoadPctRaw(snap);
    const prv = getLoadPctRaw(prevSnap);
    if (cur > prv) return 'up';
    if (cur < prv) return 'down';
  }
  return 'flat';
}

export function formatCellOccupancyLabel(snap) {
  if (!snap) {
    return { primary: '—', secondary: 'rooms' };
  }
  if (snap.mode === 'inventory') {
    return {
      primary: `${snap.bookedNights}/${snap.capacity || 0}`,
      secondary: 'nights',
    };
  }
  return {
    primary: `${snap.occupied}/${snap.totalRooms || 0}`,
    secondary: 'rooms',
  };
}

export function formatDaySummary(snap, day) {
  if (snap.mode === 'inventory') {
    return `${day.format('ddd, D MMM')}: ${snap.arrivals} arrivals · ${snap.bookedNights}/${snap.capacity} nights (${snap.bookedPctRaw.toFixed(1)}%)`;
  }
  return `${day.format('ddd, D MMM')}: ${snap.arrivals} arrivals · ${snap.occupied}/${snap.totalRooms} rooms (${Number(snap.occupancyPctRaw ?? snap.occupancyPct).toFixed(1)}%)`;
}
